import { useEffect, useMemo, useState } from "react"

import { ClipboardIcon } from "@heroicons/react/24/outline"
import { Outlet, useLoaderData, useSearchParams } from "react-router-dom"

import { DashboardContent } from "../Dashboard/DashboardContent"
import { DateRangeFilter } from "../Dashboard/DateRangeFilter"
import { StoreFilter } from "../Dashboard/StoreFilter"
import { StorefrontInsightsOrderVolumePanel } from "./StorefrontInsightsOrderVolumePanel"
import { StorefrontInsightsOverviewPanel } from "./StorefrontInsightsOverviewPanel"
import { StorefrontInsightsPopularProductsPanel } from "./StorefrontInsightsPopularProductsPanel"
import { StorefrontInsightsRecipientReactionsPanel } from "./StorefrontInsightsRecipientReactionsPanel"
import { StorefrontInsightsShippingDestinationsPanel } from "./StorefrontInsightsShippingDestinationsPanel"
import { StorefrontInsightsThankYouNotesPanel } from "./StorefrontInsightsThankYouNotesPanel"
import { StorefrontInsightsTopUsersPanel } from "./StorefrontInsightsTopUsersPanel"
import { Bee } from "~/src/components/BeeKit"
import { MainHeading } from "~/src/components/MainHeading"

const DATE_RANGE_START_PARAM_KEY = "start_date"
const DATE_RANGE_END_PARAM_KEY = "end_date"
const DATE_RANGE_ALL_TIME_PARAM_KEY = "all_time"

const STORE_PARAM_KEY = "store_ids[]"
const STORE_PARAM_ALL_STORES_KEY = "all_stores"
//region Types
export type DashboardStorefrontInsightsOutletContext = {
  storeFilterSelections: string[]
  startDate: Date | null
  endDate: Date | null
  allTime: boolean
  userEmail?: string
  generateReportURL: string
  setFlashState: (flash: { success?: string; error?: string } | null) => void
}

export type DashboardStorefrontInsightsProps = {
  stores: Record<string, string>
  userEmail: string
}

//endregion

//region Component

export function DashboardStorefrontInsightsShow() {
  const { stores } = useLoaderData() as DashboardStorefrontInsightsProps
  const [searchParams, setSearchParams] = useSearchParams()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [allTime, setAllTime] = useState<boolean>(false)
  const [storeFilterSelections, setStoreFilterSelections] = useState<string[]>([])
  const generateReportUrl = `generate_report?${searchParams.toString()}`

  //region Flash messages
  const [flashState, setFlashState] = useState<{ success?: string; error?: string } | null>(null)

  // Clear flash messages
  const clearFlashMessage = () => {
    setFlashState(null)
  }
  //endregion

  useEffect(() => {
    let selections: string[]
    if (searchParams.get(STORE_PARAM_ALL_STORES_KEY) === "true") {
      selections = Object.keys(stores)
    } else {
      selections = [...searchParams.getAll(STORE_PARAM_KEY)]
    }
    const startString = searchParams.get(DATE_RANGE_START_PARAM_KEY)
    setStartDate(startString ? new Date(startString) : null)

    const endString = searchParams.get(DATE_RANGE_END_PARAM_KEY)
    setEndDate(endString ? new Date(endString) : null)
    setAllTime(searchParams.get(DATE_RANGE_ALL_TIME_PARAM_KEY) == "true")

    setStoreFilterSelections(selections)
  }, [searchParams])

  const outletContext = useMemo(
    () => ({ storeFilterSelections, startDate, endDate, allTime, generateReportUrl, setFlashState }),
    [storeFilterSelections, startDate, endDate, allTime, generateReportUrl, setFlashState]
  )

  //region Event Handlers
  //endregion

  //region JSX
  return (
    <>
      {/* Flash messages */}
      {flashState?.success && (
        <Bee.Flash kind="success" onClose={clearFlashMessage}>
          {flashState.success}
        </Bee.Flash>
      )}
      {flashState?.error && (
        <Bee.Flash kind="error" onClose={clearFlashMessage}>
          {flashState.error}
        </Bee.Flash>
      )}

      <DashboardContent className="@container flex flex-col gap-4">
        <MainHeading
          title="Storefront Insights"
          description="The information below is a snapshot of your Storefront performance and excludes any orders placed through a Preferred Gift Campaign form or a Quick or Surprise Send. To see all order, user, & product data, generate a report."
          className="mr-auto ml-0"
        />

        {/* Top level filters + Report button */}
        <div className="flex flex-col flex-wrap gap-3 @lg:flex-row @lg:items-center @lg:justify-between">
          {/* Filters */}
          <div className="flex flex-0 shrink-1 flex-col gap-3 @lg:flex-row">
            <div className="w-full flex-0 shrink-1">
              <StoreFilter
                options={stores}
                selections={storeFilterSelections}
                onChange={(newSelections) => {
                  setSearchParams((prev) => {
                    prev.delete(STORE_PARAM_KEY)
                    prev.delete(STORE_PARAM_ALL_STORES_KEY)
                    if (newSelections.length == Object.keys(stores).length)
                      prev.append(STORE_PARAM_ALL_STORES_KEY, "true")
                    else newSelections.forEach((value) => prev.append(STORE_PARAM_KEY, value))
                    return prev
                  })
                }}
              />
            </div>

            <div className="w-full flex-0">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                allTime={allTime}
                onChange={(startDate, endDate, allTime) => {
                  setSearchParams((prev) => {
                    if (allTime) {
                      prev.set(DATE_RANGE_ALL_TIME_PARAM_KEY, "true")
                      prev.delete(DATE_RANGE_START_PARAM_KEY)
                      prev.delete(DATE_RANGE_END_PARAM_KEY)
                    } else {
                      prev.delete(DATE_RANGE_ALL_TIME_PARAM_KEY)
                      if (startDate) {
                        prev.set(DATE_RANGE_START_PARAM_KEY, startDate.toISOString())
                      } else {
                        prev.delete(DATE_RANGE_START_PARAM_KEY)
                      }
                      if (endDate) {
                        prev.set(DATE_RANGE_END_PARAM_KEY, endDate.toISOString())
                      } else {
                        prev.delete(DATE_RANGE_END_PARAM_KEY)
                      }
                    }
                    return prev
                  })
                }}
              />
            </div>
          </div>
          {/* Generate Report Button */}
          <div className="flex">
            <Bee.ButtonLink
              to={generateReportUrl}
              className="flex w-full items-center justify-center font-normal"
              kind="secondary"
            >
              <ClipboardIcon className="mr-2 h-4 w-4" />
              Generate report
            </Bee.ButtonLink>
          </div>
        </div>
        {/* Grid Layout for Panels */}
        <div className="grid grid-cols-1 gap-6 @3xl:auto-rows-[12rem] @3xl:grid-cols-2">
          <section className="row-span-2 @3xl:col-span-2 @3xl:row-span-1">
            <StorefrontInsightsOverviewPanel />
          </section>

          <section className="@3xl:row-span-2">
            <StorefrontInsightsOrderVolumePanel />
          </section>
          <section className="@3xl:row-span-2">
            <StorefrontInsightsPopularProductsPanel />
          </section>

          <section className="@3xl:row-span-2">
            <StorefrontInsightsTopUsersPanel />
          </section>
          <section className="@3xl:row-span-2">
            <StorefrontInsightsShippingDestinationsPanel />
          </section>

          <section className="@3xl:row-span-2">
            <StorefrontInsightsRecipientReactionsPanel />
          </section>
          <section className="@3xl:row-span-2">
            <StorefrontInsightsThankYouNotesPanel />
          </section>
        </div>
        <Outlet context={outletContext} />
      </DashboardContent>
    </>
  )
  //endregion
}
//endregion

//region Private
//endregion
